import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";
import { FormRecordProvider } from "../../../../common/controls/forms/FormRecordContext";
import FormExtras from "../../../../common/components/formExtras";
import { DownloadIcon } from "../../../../icons";
import { VisibilityIcon } from "../../../../icons";
import { PDFViewerDialog } from "../../../../common/components/PDFViewerDialog";
import { isOriginalCrudStatus, RowCrudStatus } from "../../../common/types";
import { PaymentReceiptUpdateStore } from "../../store";
import { FileCRUDType } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { LoadFilesCRUDCompact } from "../../../common/controls";
import { ShowReference } from "../../../common/controls";
import { ShowNumber } from "../../../common/controls";
import { InputDateTime } from "../../../common/controls";
import { ShowItemsCompact } from "../../../common/controls";
import { CrudRow } from "../../../common/types";
import { GridColumn } from "../../../common/types";
import { FormControl } from "@mui/material";

export type PaymentReceiptUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  recordId?: string;
  paymentReceiptOnRestoreFile: (id: string) => void;
  paymentReceiptOnLoadFile: (file: any) => void;
  paymentReceiptOnCommitFile: (changes: GridChangeSet) => void;
  paymentReceiptFiles: FileCRUDType[];
  paymentReceiptError?: string;
  typeSelectedValue?: { displayId: string; id: string } | null;
  onTypeSelected?: (value: { displayId: string; id: string } | null) => void;
  typeOptions?: { displayId: string; id: string }[];
  typeError?: string;
  amount: number;
  amountSelectedValue?: { displayId: string; id: string } | null;
  amountError?: string;
  issueDateTime: Date | null;
  issueDateTimeSelectedValue?: { displayId: string; id: string } | null;
  onIssueDateTimeChange?: (value: Date | null) => void;
  issueDateTimeError?: string;
  totalUses: number;
  totalUsesSelectedValue?: { displayId: string; id: string } | null;
  totalUsesError?: string;
  remainingUses: number;
  remainingUsesSelectedValue?: { displayId: string; id: string } | null;
  remainingUsesError?: string;
  usesRows: CrudRow[];
  usesVisibleColumns: GridColumn[];
  dummyCRUDRestoredObservable: number;
  paymentReceiptUpdateStore: PaymentReceiptUpdateStore;
};

export const PaymentReceiptUpdateForm: React.FC<PaymentReceiptUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      recordId,
      paymentReceiptOnRestoreFile,
      paymentReceiptOnLoadFile,
      paymentReceiptOnCommitFile,
      paymentReceiptFiles,
      paymentReceiptError,
      typeSelectedValue = null,
      typeError,
      amount = 0,
      amountError,
      issueDateTime = null,
      issueDateTimeError,
      onIssueDateTimeChange = () => {},
      totalUses = 0,
      totalUsesError,
      remainingUses = 0,
      remainingUsesError,
      usesRows,
      usesVisibleColumns,
      dummyCRUDRestoredObservable,
      paymentReceiptUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const getViewAction = (
        onDownload: (props: {
          id: string;
          type: string;
          name: string;
          __status__: RowCrudStatus;
        }) => void,
        onViewPdf?: (props: any) => void
      ) => {
        const actions: any[] = [];
        if (onViewPdf) {
          actions.push({
            columnName: "__action_view_pdf",
            onClick: (props: any) =>
              isOriginalCrudStatus(props.__status__) ? onViewPdf(props) : {},
            icon: <VisibilityIcon />,
            label: langStore.viewCommand,
            isActionEnabled: (props: any) =>
              props.name?.toLowerCase().endsWith(".pdf"),
          });
        }
        actions.push({
          columnName: "__action_view",
          onClick: (props: any) =>
            isOriginalCrudStatus(props.__status__) ? onDownload(props) : {},
          icon: <DownloadIcon />,
          label: langStore.downloadCommand,
        });
        return actions;
      };
      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormRecordProvider
              recordId={recordId}
              values={{
                type: typeSelectedValue,
                amount,
                issueDateTime,
                totalUses,
                remainingUses,
              }}
            >
              <FormExtras
                entity="PaymentReceipt"
                mode="update"
                recordId={recordId}
                values={{
                  type: typeSelectedValue,
                  amount,
                  issueDateTime,
                  totalUses,
                  remainingUses,
                }}
                options={{}}
                onPatch={(campo: string, valor: any) => {
                  switch (campo) {
                    case "issueDateTime":
                      onIssueDateTimeChange?.(valor);
                      break;
                  }
                }}
              />
              <GroupContainer
                id="null"
                errorText={paymentReceiptUpdateStore.getCardErrors("null")}
                title={langStore.groupPaymentReceiptLabel}
                collapsible={false}
              >
                <FormControl fullWidth>
                  <LoadFilesCRUDCompact
                    rows={paymentReceiptFiles}
                    maxRows={1}
                    menuActions={getViewAction(
                      (props) =>
                        paymentReceiptUpdateStore.fileDownload(
                          props,
                          "paymentReceipt"
                        ),
                      (props) =>
                        paymentReceiptUpdateStore.viewFilePdf(
                          props,
                          "paymentReceipt"
                        )
                    )}
                    showAddCommand={true}
                    showDeleteCommand={true}
                    label={langStore.PaymentReceiptPaymentReceiptLabel}
                    description={
                      langStore.PaymentReceiptPaymentReceiptDescription
                    }
                    onLoadFile={(file: File) => paymentReceiptOnLoadFile(file)}
                    columns={[
                      { name: "mimetype", title: langStore.fileTypeLabel },
                      { name: "name", title: langStore.fileNameLabel },
                      { name: "formattedSize", title: langStore.fileSizeLabel },
                    ]}
                    onRestore={(id: string) => paymentReceiptOnRestoreFile(id)}
                    onCommitChanges={(changes: GridChangeSet) =>
                      paymentReceiptOnCommitFile(changes)
                    }
                    dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                    error={paymentReceiptError}
                  />
                  <ShowReference
                    label={langStore.PaymentReceiptTypeLabel}
                    selectedValue={typeSelectedValue?.displayId ?? ""}
                    helperText={typeError}
                    description={langStore.PaymentReceiptTypeDescription}
                  />
                  <ShowNumber
                    value={amount}
                    error={Boolean(amountError)}
                    helperText={
                      amountError ||
                      paymentReceiptUpdateStore.warnings["amount"]?.message
                    }
                    label={langStore.PaymentReceiptAmountLabel}
                    decimalScale={2}
                    description={langStore.PaymentReceiptAmountDescription}
                  />
                  <InputDateTime
                    autoFocus
                    value={issueDateTime}
                    error={Boolean(issueDateTimeError)}
                    warning={
                      !issueDateTimeError &&
                      Boolean(
                        paymentReceiptUpdateStore.warnings["issueDateTime"]
                      )
                    }
                    helperText={
                      issueDateTimeError ||
                      paymentReceiptUpdateStore.warnings["issueDateTime"]
                        ?.message
                    }
                    label={langStore.PaymentReceiptIssueDateTimeLabel}
                    nullText={langStore.optionalFieldHint}
                    description={
                      langStore.PaymentReceiptIssueDateTimeDescription
                    }
                    onChange={(value: Date | null | null) =>
                      onIssueDateTimeChange(value)
                    }
                  />
                  <ShowNumber
                    value={totalUses}
                    error={Boolean(totalUsesError)}
                    helperText={
                      totalUsesError ||
                      paymentReceiptUpdateStore.warnings["totalUses"]?.message
                    }
                    label={langStore.PaymentReceiptTotalUsesLabel}
                    decimalScale={0}
                    description={langStore.PaymentReceiptTotalUsesDescription}
                  />
                  <ShowNumber
                    value={remainingUses}
                    error={Boolean(remainingUsesError)}
                    helperText={
                      remainingUsesError ||
                      paymentReceiptUpdateStore.warnings["remainingUses"]
                        ?.message
                    }
                    label={langStore.PaymentReceiptRemainingUsesLabel}
                    decimalScale={0}
                    description={
                      langStore.PaymentReceiptRemainingUsesDescription
                    }
                  />
                  <ShowItemsCompact
                    label={langStore.PaymentReceiptUsesLabel}
                    description={langStore.PaymentReceiptUsesDescription}
                    showLabel={true}
                    rows={usesRows}
                    columns={usesVisibleColumns}
                    entityName="PaymentReceiptUse"
                    enableDownload={true}
                    onRowClick={(rowId) =>
                      rootStore.openSubentityModal("paymentReceiptUse", rowId)
                    }
                  />
                </FormControl>
              </GroupContainer>
            </FormRecordProvider>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
          <PDFViewerDialog
            open={paymentReceiptUpdateStore.pdfViewerOpen}
            onClose={() => paymentReceiptUpdateStore.closePdfViewer()}
            pdfUrl={paymentReceiptUpdateStore.pdfViewerUrl}
            title={paymentReceiptUpdateStore.pdfViewerTitle}
          />
        </Grid>
      );
    }
  );
